import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyCredits,
  fetchCreditLotEvents,
  fetchCreditLots,
  fetchCreditPurchase,
  fetchCreditSummary,
  fetchCreditTransfers,
  fetchOrgCreditBalance,
  fetchTransferable,
  holdCreditLot,
  releaseCreditLot,
  reverseCreditLot,
  transferCredits,
} from "@/api/credits";
import { PURCHASE_STATUS, type TransferAllocation } from "@/interfaces/credits";

export const summaryKey = (groupId: number) => ["creditSummary", groupId] as const;
export const lotsKey = (groupId: number) => ["creditLots", groupId] as const;

/** Refresh everything a lot action touches: the lots list, the summary box, and every balance
 *  rollup (Groups list, OrgDetail, orgs blotter). Mirrors useBuyCredits's invalidation. */
function useLotActionInvalidators(groupId: number) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: lotsKey(groupId) });
    qc.invalidateQueries({ queryKey: summaryKey(groupId) });
    qc.invalidateQueries({ queryKey: ["groups"] });
    qc.invalidateQueries({ queryKey: ["orgCreditBalance"] });
    qc.invalidateQueries({ queryKey: ["organizations"] });
  };
}

/**
 * The wallet summary box. Disabled until a groupId is present.
 *
 * `until` switches `available` from "free today" to "what a campaign running to this date may be
 * funded with". It is part of the query key, so changing a campaign's expiry refetches rather than
 * showing a figure computed against the previous date.
 */
export function useCreditSummary(groupId: number | null | undefined, until?: string | null) {
  return useQuery({
    queryKey: ["creditSummary", groupId, until ?? null],
    queryFn: () => fetchCreditSummary(groupId as number, until),
    enabled: groupId != null,
  });
}

/** An organization's total credit pool. */
export function useOrgCreditBalance(orgId: number | null | undefined) {
  return useQuery({
    queryKey: ["orgCreditBalance", orgId],
    queryFn: () => fetchOrgCreditBalance(orgId as number),
    enabled: orgId != null,
  });
}

/** The group's lots. `all` = every state (Credits Pool management); default = active-only. */
export function useCreditLots(groupId: number | null | undefined, all = false) {
  return useQuery({
    queryKey: ["creditLots", groupId, all],
    queryFn: () => fetchCreditLots(groupId as number, all),
    enabled: groupId != null,
  });
}

/** A lot's activity history (only fetched when a lot is selected). */
export function useCreditLotEvents(groupId: number, lotId: number | null) {
  return useQuery({
    queryKey: ["creditLotEvents", groupId, lotId],
    queryFn: () => fetchCreditLotEvents(groupId, lotId as number),
    enabled: lotId != null,
  });
}

/** Hold a lot (Credits Pool). */
export function useHoldCreditLot(groupId: number) {
  const invalidate = useLotActionInvalidators(groupId);
  return useMutation({
    mutationFn: (args: { lotId: number; reason: string }) =>
      holdCreditLot(groupId, args.lotId, args.reason),
    onSuccess: invalidate,
  });
}

/** Release a held lot (Credits Pool). */
export function useReleaseCreditLot(groupId: number) {
  const invalidate = useLotActionInvalidators(groupId);
  return useMutation({
    mutationFn: (args: { lotId: number }) => releaseCreditLot(groupId, args.lotId),
    onSuccess: invalidate,
  });
}

/** Reverse a lot — full clawback (Credits Pool). */
export function useReverseCreditLot(groupId: number) {
  const invalidate = useLotActionInvalidators(groupId);
  return useMutation({
    mutationFn: (args: { lotId: number; reason: string }) =>
      reverseCreditLot(groupId, args.lotId, args.reason),
    onSuccess: invalidate,
  });
}

/**
 * A settled purchase minted a lot: refresh the wallet (summary + lots) and every view that shows a
 * group's / org's balance: the Groups-tab list (each row's Credit pool + Total pooled), OrgDetail's
 * Credit balance, and the orgs blotter's Credit Balance column. Prefix match (no orgId here) —
 * react-query invalidates any key starting with these.
 */
function invalidatePurchasedCredits(queryClient: QueryClient, groupId: number) {
  queryClient.invalidateQueries({ queryKey: summaryKey(groupId) });
  queryClient.invalidateQueries({ queryKey: lotsKey(groupId) });
  queryClient.invalidateQueries({ queryKey: ["groups"] });
  queryClient.invalidateQueries({ queryKey: ["orgCreditBalance"] });
  queryClient.invalidateQueries({ queryKey: ["organizations"] });
}

/**
 * Buys credits, then POLLS the purchase until the webhook settles it — the balance moves when the
 * lot is minted (webhook), not when the POST returns. Resolves "success" | "failed" | "pending"
 * (pending = still processing after the poll budget; the webhook may just be slow). On success it
 * invalidates the summary + lots so the wallet refreshes.
 */
export function useBuyCredits(groupId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      credits: number;
      paymentMethodId: number;
      /** Cause beneficiaries for whatever is unused when these credits expire, in pick order. */
      causes?: string[];
    }) => {
      const { purchaseId } = await buyCredits(
        groupId,
        vars.credits,
        vars.paymentMethodId,
        vars.causes,
      );

      // Poll ~15s for the webhook mint. Stripe test webhooks land in a second or two.
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const purchase = await fetchCreditPurchase(groupId, purchaseId);
        if (purchase.status === PURCHASE_STATUS.success)
          return { outcome: "success" as const, purchaseId };
        if (
          purchase.status === PURCHASE_STATUS.failed ||
          purchase.status === PURCHASE_STATUS.cancelled
        ) {
          return { outcome: "failed" as const, purchaseId };
        }
      }
      return { outcome: "pending" as const, purchaseId };
    },
    onSuccess: (result) => {
      if (result.outcome === "success") invalidatePurchasedCredits(queryClient, groupId);
    },
  });
}

/**
 * Reads a purchase's status ONCE — Add Recipients' "Check again" for a purchase `useBuyCredits`
 * left pending. One that has settled since refreshes the wallet the way `useBuyCredits` does.
 */
export function useCheckCreditPurchase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { groupId: number; purchaseId: number }) =>
      fetchCreditPurchase(vars.groupId, vars.purchaseId),
    onSuccess: (purchase, vars) => {
      if (purchase.status === PURCHASE_STATUS.success) {
        invalidatePurchasedCredits(queryClient, vars.groupId);
      }
    },
  });
}

/** One page of this group's transfer history, both directions. */
export function useCreditTransfers(
  groupId: number | null | undefined,
  params: {
    search?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page?: number;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: ["creditTransfers", groupId, params],
    queryFn: () => fetchCreditTransfers(groupId as number, params),
    enabled: groupId != null,
  });
}

/**
 * What this group may send, and to whom.
 *
 * NEVER SERVED FROM CACHE. The cap moves whenever anything else touches the group — a campaign
 * launched in another tab, a send that drew credits, a lot that expired overnight — and none of
 * those invalidate this key. A stale number here is not a cosmetic problem: it is the figure
 * someone allocates against, so it would offer credits that are already spoken for and the transfer
 * would be refused at submit with no explanation the screen had given.
 *
 * `gcTime: 0` drops it the moment the modal unmounts, so reopening always asks the server. The
 * dialog is opened rarely and the read is small — there is nothing to save here and a wrong number
 * to avoid.
 */
export function useTransferable(groupId: number | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["transferable", groupId],
    queryFn: () => fetchTransferable(groupId as number),
    enabled: enabled && groupId != null,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Send credits to a sibling group.
 *
 * Invalidates BOTH ends: the source's lots and balance fall, the destination's rise, and a stale
 * wallet showing the credits still at home is the first thing anyone would notice.
 */
export function useTransferCredits(groupId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      destinationGroupId: number;
      allocations: TransferAllocation[];
      note?: string;
    }) => transferCredits(groupId, vars.destinationGroupId, vars.allocations, vars.note),
    onSuccess: (_result, vars) => {
      for (const id of [groupId, vars.destinationGroupId]) {
        queryClient.invalidateQueries({ queryKey: ["creditLots", id] });
        queryClient.invalidateQueries({ queryKey: ["creditTransfers", id] });
        queryClient.invalidateQueries({ queryKey: ["creditSummary", id] });
        queryClient.invalidateQueries({ queryKey: ["transferable", id] });
      }
      queryClient.invalidateQueries({ queryKey: ["orgCreditBalance"] });
    },
  });
}
